/**
 * GuruMascot Component
 * ====================
 * Animated compass mascot with 4 emotional states:
 * - friendly: Default/idle state (gentle float)
 * - searching: Processing/Deep Mode (spinning orbit)
 * - found: High confidence match (celebration)
 * - thinking: Needs clarification (thought bubble)
 *
 * Based on guru_mascot_concept.html design spec.
 */

import React from 'react';

// State-specific styles
const stateStyles = {
  friendly: {
    accent: '#00d4aa',
    ring: 'rgba(0, 212, 170, 0.3)',
    animation: 'float 3s ease-in-out infinite',
  },
  searching: {
    accent: '#7c5cfc',
    ring: '#7c5cfc',
    animation: 'none',
  },
  found: {
    accent: '#fbbf24',
    ring: 'rgba(251, 191, 36, 0.4)',
    animation: 'celebrate 1s ease-in-out infinite',
  },
  thinking: {
    accent: '#ff6b9d',
    ring: 'rgba(255, 107, 157, 0.2)',
    animation: 'float 3s ease-in-out infinite',
  },
};

const GuruMascot = ({
  state = 'friendly', // friendly, searching, found, thinking
  size = 48,
  className = '',
  showLabel = false,
}) => {
  const style = stateStyles[state] || stateStyles.friendly;

  return (
    <div
      className={`guru-mascot ${className}`}
      style={{
        width: size,
        height: size,
        animation: style.animation,
      }}
    >
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes celebrate {
          0%, 100% { transform: rotate(0deg) scale(1); }
          25% { transform: rotate(-8deg) scale(1.05); }
          75% { transform: rotate(8deg) scale(1.05); }
        }
        @keyframes spin-needle {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(45deg); }
          50% { transform: rotate(0deg); }
          75% { transform: rotate(-20deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes orbit-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes blink {
          0%, 90%, 100% { transform: scaleY(1); }
          95% { transform: scaleY(0.1); }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        @keyframes glow-pulse {
          0%, 100% { filter: drop-shadow(0 0 4px rgba(251, 191, 36, 0.3)); }
          50% { filter: drop-shadow(0 0 12px rgba(251, 191, 36, 0.6)); }
        }
        .guru-mascot svg {
          width: 100%;
          height: 100%;
        }
        .guru-mascot .needle-spin {
          animation: spin-needle 2s ease-in-out infinite;
          transform-origin: 60px 16px;
        }
        .guru-mascot .orbit-ring {
          animation: orbit-spin 3s linear infinite;
          transform-origin: center;
        }
        .guru-mascot .blink-eye {
          animation: blink 4s ease-in-out infinite;
        }
        .guru-mascot .sparkle {
          animation: sparkle 1.5s ease-in-out infinite;
        }
        .guru-mascot .glow {
          animation: glow-pulse 2s ease-in-out infinite;
        }
      `}</style>

      {state === 'friendly' && <FriendlyGuru style={style} />}
      {state === 'searching' && <SearchingGuru style={style} />}
      {state === 'found' && <FoundGuru style={style} />}
      {state === 'thinking' && <ThinkingGuru style={style} />}

      {showLabel && (
        <div style={{
          fontSize: '10px',
          fontWeight: 700,
          color: style.accent,
          textAlign: 'center',
          marginTop: '4px',
          textTransform: 'capitalize',
        }}>
          {state === 'found' ? 'Found it!' : state}
        </div>
      )}
    </div>
  );
};

// Friendly / Idle State
const FriendlyGuru = ({ style }) => (
  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="friendlyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00d4aa"/>
        <stop offset="100%" stopColor="#7c5cfc"/>
      </linearGradient>
      <radialGradient id="bodyGrad" cx="40%" cy="35%">
        <stop offset="0%" stopColor="#2a3a5c"/>
        <stop offset="100%" stopColor="#111827"/>
      </radialGradient>
    </defs>
    {/* Outer ring */}
    <circle cx="60" cy="60" r="52" fill="none" stroke="url(#friendlyGrad)" strokeWidth="2" opacity="0.3"/>
    {/* Body */}
    <circle cx="60" cy="60" r="44" fill="#1a2236"/>
    <circle cx="60" cy="60" r="44" fill="url(#bodyGrad)" opacity="0.6"/>
    <circle cx="60" cy="60" r="42" fill="none" stroke={style.accent} strokeWidth="1.5" opacity="0.4"/>
    {/* Eyes */}
    <ellipse className="blink-eye" cx="45" cy="52" rx="7" ry="8" fill="#fff"/>
    <ellipse className="blink-eye" cx="75" cy="52" rx="7" ry="8" fill="#fff"/>
    <circle cx="45" cy="53" r="4" fill="#1a1a2e"/>
    <circle cx="75" cy="53" r="4" fill="#1a1a2e"/>
    <circle cx="47" cy="51" r="1.5" fill="#fff"/>
    <circle cx="77" cy="51" r="1.5" fill="#fff"/>
    {/* Smile */}
    <path d="M45 68 Q60 80 75 68" fill="none" stroke={style.accent} strokeWidth="2.5" strokeLinecap="round"/>
    {/* Compass needle */}
    <line x1="60" y1="25" x2="60" y2="12" stroke={style.accent} strokeWidth="2.5" strokeLinecap="round"/>
    <polygon points="60,8 56,18 64,18" fill={style.accent}/>
  </svg>
);

// Searching / Processing State
const SearchingGuru = ({ style }) => (
  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="searchBodyGrad" cx="40%" cy="35%">
        <stop offset="0%" stopColor="#2a3a5c"/>
        <stop offset="100%" stopColor="#111827"/>
      </radialGradient>
    </defs>
    {/* Orbit ring (spinning) */}
    <circle
      className="orbit-ring"
      cx="60" cy="60" r="56"
      fill="none"
      stroke={style.accent}
      strokeWidth="1"
      strokeDasharray="4,8"
    />
    {/* Body */}
    <circle cx="60" cy="60" r="44" fill="#1a2236"/>
    <circle cx="60" cy="60" r="44" fill="url(#searchBodyGrad)" opacity="0.6"/>
    <circle cx="60" cy="60" r="42" fill="none" stroke={style.accent} strokeWidth="1.5" opacity="0.4"/>
    {/* Focused eyes (narrowed) */}
    <ellipse cx="45" cy="54" rx="7" ry="5" fill="#fff"/>
    <ellipse cx="75" cy="54" rx="7" ry="5" fill="#fff"/>
    <circle cx="47" cy="54" r="3.5" fill="#1a1a2e"/>
    <circle cx="77" cy="54" r="3.5" fill="#1a1a2e"/>
    {/* Determined mouth */}
    <line x1="48" y1="70" x2="72" y2="70" stroke={style.accent} strokeWidth="2" strokeLinecap="round"/>
    {/* Spinning needle */}
    <g className="needle-spin">
      <line x1="60" y1="25" x2="60" y2="10" stroke={style.accent} strokeWidth="2.5" strokeLinecap="round"/>
      <polygon points="60,6 56,16 64,16" fill={style.accent}/>
    </g>
    {/* Search sparkles */}
    <circle className="sparkle" cx="95" cy="30" r="2" fill={style.accent} opacity="0.7"/>
    <circle className="sparkle" style={{animationDelay: '0.5s'}} cx="25" cy="85" r="1.5" fill="#00d4aa" opacity="0.5"/>
    <circle className="sparkle" style={{animationDelay: '0.3s'}} cx="100" cy="80" r="1.5" fill="#ff6b9d" opacity="0.5"/>
  </svg>
);

// Found It / Excited State
const FoundGuru = ({ style }) => (
  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" className="glow">
    <defs>
      <radialGradient id="foundBodyGrad" cx="40%" cy="35%">
        <stop offset="0%" stopColor="#2a3a5c"/>
        <stop offset="100%" stopColor="#111827"/>
      </radialGradient>
    </defs>
    {/* Celebration ring */}
    <circle cx="60" cy="60" r="52" fill="none" stroke={style.accent} strokeWidth="3" opacity="0.4"/>
    {/* Body */}
    <circle cx="60" cy="60" r="44" fill="#1a2236"/>
    <circle cx="60" cy="60" r="44" fill="url(#foundBodyGrad)" opacity="0.6"/>
    <circle cx="60" cy="60" r="42" fill="none" stroke={style.accent} strokeWidth="1.5" opacity="0.5"/>
    {/* Big happy eyes */}
    <ellipse cx="43" cy="50" rx="9" ry="10" fill="#fff"/>
    <ellipse cx="77" cy="50" rx="9" ry="10" fill="#fff"/>
    <circle cx="44" cy="50" r="5" fill="#1a1a2e"/>
    <circle cx="78" cy="50" r="5" fill="#1a1a2e"/>
    <circle cx="46" cy="48" r="2" fill="#fff"/>
    <circle cx="80" cy="48" r="2" fill="#fff"/>
    {/* Star eyes sparkle */}
    <circle className="sparkle" cx="43" cy="43" r="1.5" fill={style.accent}/>
    <circle className="sparkle" style={{animationDelay: '0.5s'}} cx="77" cy="43" r="1.5" fill={style.accent}/>
    {/* Big smile */}
    <path d="M40 66 Q60 84 80 66" fill="none" stroke={style.accent} strokeWidth="3" strokeLinecap="round"/>
    {/* Needle pointing up */}
    <line x1="60" y1="22" x2="60" y2="8" stroke={style.accent} strokeWidth="3" strokeLinecap="round"/>
    <polygon points="60,4 55,14 65,14" fill={style.accent}/>
    {/* Celebration sparkles */}
    <text x="20" y="30" fontSize="14" fill={style.accent} opacity="0.8">✦</text>
    <text x="92" y="25" fontSize="10" fill="#00d4aa" opacity="0.7">✦</text>
    <text x="100" y="95" fontSize="12" fill="#7c5cfc" opacity="0.7">✦</text>
  </svg>
);

// Thinking / Unsure State
const ThinkingGuru = ({ style }) => (
  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="thinkingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00d4aa"/>
        <stop offset="100%" stopColor="#7c5cfc"/>
      </linearGradient>
      <radialGradient id="thinkBodyGrad" cx="40%" cy="35%">
        <stop offset="0%" stopColor="#2a3a5c"/>
        <stop offset="100%" stopColor="#111827"/>
      </radialGradient>
    </defs>
    {/* Outer ring */}
    <circle cx="60" cy="60" r="52" fill="none" stroke="url(#thinkingGrad)" strokeWidth="2" opacity="0.2"/>
    {/* Body */}
    <circle cx="60" cy="60" r="44" fill="#1a2236"/>
    <circle cx="60" cy="60" r="44" fill="url(#thinkBodyGrad)" opacity="0.6"/>
    <circle cx="60" cy="60" r="42" fill="none" stroke={style.accent} strokeWidth="1.5" opacity="0.3"/>
    {/* Thinking eyes (looking up-right) */}
    <ellipse cx="45" cy="52" rx="7" ry="8" fill="#fff"/>
    <ellipse cx="75" cy="52" rx="7" ry="8" fill="#fff"/>
    <circle cx="48" cy="49" r="4" fill="#1a1a2e"/>
    <circle cx="78" cy="49" r="4" fill="#1a1a2e"/>
    {/* Hmm mouth */}
    <path d="M50 70 Q60 68 70 72" fill="none" stroke={style.accent} strokeWidth="2" strokeLinecap="round"/>
    {/* Tilted needle (uncertain) */}
    <g style={{ transform: 'rotate(25deg)', transformOrigin: '60px 60px' }}>
      <line x1="60" y1="25" x2="60" y2="12" stroke={style.accent} strokeWidth="2" strokeLinecap="round"/>
      <polygon points="60,8 56,18 64,18" fill={style.accent}/>
    </g>
    {/* Thought bubble */}
    <circle cx="95" cy="28" r="8" fill="rgba(255,107,157,0.15)" stroke={style.accent} strokeWidth="1" opacity="0.6"/>
    <text x="92" y="32" fontSize="10" fill={style.accent}>?</text>
    <circle cx="87" cy="38" r="3" fill="rgba(255,107,157,0.1)" stroke={style.accent} strokeWidth="0.5" opacity="0.4"/>
  </svg>
);

export default GuruMascot;
